<template>
    <div class="searchable-select" v-click-outside="closeDropdown">
        <label v-if="label" :for="id" class="form-label">{{ label }}</label>
        
        <div class="select-container" ref="selectContainer">
            <div 
                class="select-input form-control form-control-sm" 
                :class="{ 'is-invalid': error, 'open': isOpen }"
                @click="toggleDropdown"
            >
                <span v-if="selectedOption" class="selected-text">
                    {{ selectedOption[itemText] }}
                </span>
                <span v-else class="placeholder-text">
                    {{ placeholder }}
                </span>
                <i class="fas fa-chevron-down dropdown-icon" :class="{ 'rotate': isOpen }"></i>
            </div>

            <div v-show="isOpen" class="dropdown-menu show" :style="dropdownStyle">
                <div class="search-input-container">
                    <input 
                        ref="searchInput"
                        type="text" 
                        v-model="searchQuery" 
                        class="form-control form-control-sm search-input"
                        placeholder="Buscar..."
                        @click.stop
                    />
                    <i class="fas fa-search search-icon"></i>
                </div>

                <div class="options-container">
                    <div 
                        v-if="loading" 
                        class="dropdown-item disabled text-muted text-center"
                    >
                        <i class="fas fa-spinner fa-spin"></i> Carregando...
                    </div>
                    
                    <div 
                        v-else-if="filteredOptions.length === 0" 
                        class="dropdown-item disabled text-muted"
                    >
                        Nenhum item encontrado
                    </div>
                    
                    <div
                        v-else
                        v-for="(option, index) in filteredOptions"
                        :key="index"
                        class="dropdown-item"
                        :class="{ 'active': isSelected(option) }"
                        @click="selectOption(option)"
                    >
                        {{ option[itemText] }}
                    </div>
                </div>
            </div>
        </div>

        <small v-if="error" class="text-danger">{{ error }}</small>
    </div>
</template>

<script>
export default {
    name: "SearchableSelect",
    props: {
        value: {
            type: [String, Number],
            default: ""
        },
        options: {
            type: Array,
            required: true,
            default: () => []
        },
        itemText: {
            type: String,
            default: "name"
        },
        itemValue: {
            type: String,
            default: "id"
        },
        label: {
            type: String,
            default: ""
        },
        placeholder: {
            type: String,
            default: "-- Selecione --"
        },
        error: {
            type: String,
            default: ""
        },
        id: {
            type: String,
            default: () => `searchable-select-${Math.random().toString(36).substr(2, 9)}`
        },
        remoteSearch: {
            type: Boolean,
            default: false
        },
        loading: {
            type: Boolean,
            default: false
        },
        debounceDelay: {
            type: Number,
            default: 500
        },
        minSearchLength: {
            type: Number,
            default: 0
        }
    },
    data() {
        return {
            isOpen: false,
            searchQuery: "",
            debounceTimer: null,
            dropdownCoords: null
        };
    },
    computed: {
        selectedOption() {
            if (!this.value) return null;
            return this.options.find(option => option[this.itemValue] === this.value);
        },
        dropdownStyle() {
            if (!this.isOpen || !this.dropdownCoords) return {};
            return {
                position: 'fixed',
                top: this.dropdownCoords.top + 'px',
                left: this.dropdownCoords.left + 'px',
                width: this.dropdownCoords.width + 'px',
                zIndex: 9999
            };
        },
        filteredOptions() {
            let options = [];
            
            // Se estiver usando busca remota, retorna as opções diretamente
            if (this.remoteSearch) {
                options = this.options;
            } else {
                // Busca local
                if (!this.searchQuery) {
                    options = this.options;
                } else {
                    const query = this.searchQuery.toLowerCase();
                    options = this.options.filter(option => {
                        const text = option[this.itemText].toString().toLowerCase();
                        return text.includes(query);
                    });
                }
            }
            
            // Garante que a opção selecionada esteja sempre presente
            if (this.selectedOption) {
                const selectedExists = options.some(option => 
                    option[this.itemValue] === this.selectedOption[this.itemValue]
                );
                
                if (!selectedExists) {
                    // Adiciona a opção selecionada no início da lista
                    options = [this.selectedOption, ...options];
                }
            }
            
            return options;
        }
    },
    watch: {
        searchQuery(newValue) {
            if (this.remoteSearch) {
                this.handleRemoteSearch(newValue);
            }
        }
    },
    methods: {
        toggleDropdown() {
            this.isOpen = !this.isOpen;
            if (this.isOpen) {
                const rect = this.$refs.selectContainer.getBoundingClientRect();
                this.dropdownCoords = {
                    top: rect.bottom,
                    left: rect.left,
                    width: rect.width
                };
                this.$nextTick(() => {
                    this.$refs.searchInput?.focus();
                    // Se usar busca remota e não houver opções, fazer busca inicial
                    if (this.remoteSearch && this.options.length === 0) {
                        this.handleRemoteSearch('');
                    }
                });
            }
        },
        closeDropdown() {
            this.isOpen = false;
            this.searchQuery = "";
        },
        selectOption(option) {
            this.$emit('input', option[this.itemValue]);
            this.$emit('change', option);
            this.closeDropdown();
        },
        isSelected(option) {
            return option[this.itemValue] === this.value;
        },
        handleRemoteSearch(query) {
            // Limpa o timer anterior
            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer);
            }

            // Se a busca for menor que o mínimo requerido, emite com string vazia
            if (query.length < this.minSearchLength && query.length > 0) {
                return;
            }

            // Cria novo timer com debounce
            this.debounceTimer = setTimeout(() => {
                this.$emit('search', query);
            }, this.debounceDelay);
        }
    },
    beforeDestroy() {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }
    },
    directives: {
        'click-outside': {
            bind(el, binding) {
                el.clickOutsideEvent = function(event) {
                    if (!(el === event.target || el.contains(event.target))) {
                        binding.value(event);
                    }
                };
                document.addEventListener('click', el.clickOutsideEvent);
            },
            unbind(el) {
                document.removeEventListener('click', el.clickOutsideEvent);
            }
        }
    }
};
</script>

<style scoped>
.searchable-select {
    position: relative;
}

.select-container {
    position: relative;
}

.select-input {
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.375rem 0.75rem;
    background-color: white;
    user-select: none;
}

.select-input.open {
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.select-input.is-invalid {
    border-color: #dc3545;
}

.selected-text {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.placeholder-text {
    color: #6c757d;
}

.dropdown-icon {
    font-size: 0.75rem;
    transition: transform 0.2s;
    margin-left: 0.5rem;
}

.dropdown-icon.rotate {
    transform: rotate(180deg);
}

.dropdown-menu {
    position: fixed;
    z-index: 9999;
    max-height: 300px;
    overflow: hidden;
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
    border: 1px solid rgba(0, 0, 0, 0.15);
    padding: 0;
    background-color: #fff;
}

.search-input-container {
    position: relative;
    padding: 0.5rem;
    border-bottom: 1px solid #dee2e6;
    background-color: #f8f9fa;
}

.search-input {
    padding-right: 2rem;
}

.search-icon {
    position: absolute;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #6c757d;
    pointer-events: none;
}

.options-container {
    max-height: 240px;
    overflow-y: auto;
}

.dropdown-item {
    cursor: pointer;
    padding: 0.5rem 1rem;
    transition: background-color 0.15s;
}

.dropdown-item:hover:not(.disabled) {
    background-color: #f8f9fa;
}

.dropdown-item.active {
    background-color: #007bff;
    color: white;
}

.dropdown-item.disabled {
    cursor: not-allowed;
}

/* Scrollbar customization */
.options-container::-webkit-scrollbar {
    width: 6px;
}

.options-container::-webkit-scrollbar-track {
    background: #f1f1f1;
}

.options-container::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
}

.options-container::-webkit-scrollbar-thumb:hover {
    background: #555;
}
</style>
